import { useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { learningService } from '../services/learningService';
import type { LetterPickerErrorData } from '../components/learning/LetterPicker';

type SourceMode = 'dictation' | 'comprehension' | 'quiz' | 'vocabulary' | 'flashcard' | 'placement_test';

interface SentenceErrorEntry {
  expectedText: string;
  totalLetters: number;
  errors: LetterPickerErrorData['errors'];
  revealedPositions: number[];
  expectedLetters: string[];
}

/** Debounce delay before flushing accumulated errors (ms) */
const FLUSH_DELAY = 3000;

/**
 * Accumulates LetterPicker error data across sentences and auto-flushes
 * to the phonics-errors API after a debounced delay.
 */
export function usePhonicsErrorCollector(courseId: string | undefined, sourceMode: SourceMode) {
  const { user } = useAuth();
  const sentencesRef = useRef<SentenceErrorEntry[]>([]);
  const flushingRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Stable refs for flush
  const courseIdRef = useRef(courseId);
  const sourceModeRef = useRef(sourceMode);
  const userRef = useRef(user);
  courseIdRef.current = courseId;
  sourceModeRef.current = sourceMode;
  userRef.current = user;

  const doFlush = useCallback(async () => {
    if (!userRef.current || sentencesRef.current.length === 0 || flushingRef.current) return;
    flushingRef.current = true;

    const sentences = [...sentencesRef.current];
    sentencesRef.current = [];

    try {
      await learningService.submitPhonicsErrors({
        courseId: courseIdRef.current,
        sourceMode: sourceModeRef.current,
        sentences,
      });
    } catch {
      // Best-effort analytics — don't block UX
    } finally {
      flushingRef.current = false;
    }
  }, []);

  const scheduleFlush = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => doFlush(), FLUSH_DELAY);
  }, [doFlush]);

  const addErrors = useCallback((data: LetterPickerErrorData) => {
    // Always collect — even perfect sentences contribute to encounter counts
    sentencesRef.current.push({
      expectedText: data.expectedText,
      totalLetters: data.totalLetters,
      errors: data.errors,
      revealedPositions: data.revealedPositions,
      expectedLetters: data.expectedLetters || [],
    });

    // Auto-flush after debounce
    scheduleFlush();
  }, [scheduleFlush]);

  // Flush on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      // Fire synchronous flush attempt
      doFlush();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { addErrors, flush: doFlush };
}
